import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const VIAL = "https://customer-assets.emergentagent.com/job_peptide-marketplace-8/artifacts/e38rr370_TB-500%20PDC-1-%20White.png";

const steps = [
  { n: "01", t: "Purity", d: "Every compound begins at ≥99% purity, synthesized under exacting conditions." },
  { n: "02", t: "Independent Testing", d: "Third-party laboratories verify identity and concentration via HPLC and mass spectrometry." },
  { n: "03", t: "Quality Control", d: "Each lot is inspected and cross-checked against reference standards." },
  { n: "04", t: "Certificate of Analysis", d: "A full COA accompanies every batch — complete transparency, always." },
  { n: "05", t: "Packaging", d: "Sealed, protected, and prepared for cold-chain handling where required." },
  { n: "06", t: "Shipment", d: "Discreet, fast North American dispatch — research-ready on arrival." },
];

function Step({ index, count, scrollYProgress, step }) {
  const seg = 1 / count;
  const start = index * seg;
  const opacity = useTransform(
    scrollYProgress,
    [start - seg * 0.4, start, start + seg * 0.6, start + seg],
    [0, 1, 1, 0]
  );
  const y = useTransform(scrollYProgress, [start - seg * 0.4, start + seg], [40, -40]);
  return (
    <motion.div style={{ opacity, y }} className="absolute inset-0 flex flex-col justify-center">
      <span className="font-display text-[#4ADE80] text-sm tracking-[0.3em]">{step.n}</span>
      <h3 className="mt-4 font-serif-editorial text-5xl md:text-6xl text-[#F5F5F5] leading-none">{step.t}</h3>
      <p className="mt-6 max-w-md text-lg text-[#A8A8A8] font-light leading-relaxed">{step.d}</p>
    </motion.div>
  );
}

export default function ProcessScroll() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-6, 6]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1.05, 0.95]);

  return (
    <section id="process" ref={ref} data-testid="process-scroll" className="relative bg-[#0F0F10]" style={{ height: "320vh" }}>
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="mx-auto max-w-[1500px] w-full px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Sticky vial */}
          <div className="relative flex justify-center order-2 lg:order-1">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-[60%] w-[60%] rounded-full bg-[#4ADE80]/10 blur-[110px]" />
            </div>
            <motion.img
              src={VIAL}
              alt="EndaG1 vial through the quality process"
              style={{ rotate, scale }}
              className="relative z-10 max-h-[60vh] w-auto object-contain vial-blend-soft drop-shadow-[0_40px_80px_rgba(0,0,0,0.6)]"
            />
          </div>
          {/* Steps */}
          <div className="relative h-[260px] order-1 lg:order-2">
            <p className="absolute -top-16 text-[11px] uppercase tracking-[0.3em] text-[#4ADE80]">From Synthesis to Research-Ready</p>
            {steps.map((s, i) => (
              <Step key={s.n} index={i} count={steps.length} scrollYProgress={scrollYProgress} step={s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
